import { useTranslation } from 'react-i18next'

import { EMPTY_OBJECT } from '../../../../constants/common'
import { useNotification } from '../../../../hooks'
import {
  getMassActionExcludeIdParam,
  getMassActionIdParam,
} from '../../../../utils/common'
import { getIdFilterParam } from '../../../../utils/filter'
import { ConfirmationModal } from '../../../Confirmation'
import { getMassActionsSuccessItemsLength } from '../../DocFlow.utils'
import {
  deleteSendingItem,
  DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_ACTIVATE_SAVE_TO_PROFILE,
  DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_ACTIVATE_SEND_ADDITIONAL_EMAIL,
  DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_ACTIVATE_SEND_EMAIL,
  DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_ACTIVATE_SEND_EPOST,
  DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_DEACTIVATE_SAVE_TO_PROFILE,
  DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_DEACTIVATE_SEND_ADDITIONAL_EMAIL,
  DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_DEACTIVATE_SEND_EMAIL,
  DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_DEACTIVATE_SEND_EPOST,
  DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_DELETE,
  getDocFlowSendingResultDeleteAttentionModalText,
  getDocFlowSendingResultNotificationTitle,
  getSendingCommunicationTableMassConfirmationText,
  performDocFlowSendingMassAction,
} from '../../DocFlowSending'
import { useDocFlowContext } from '../useDocFlowContext'
import { useDocFlowSendingContext } from '../useDocFlowSendingContext'

import { isDeleteMssAction } from './useSendingTableMassActions.utils'

export function useSendingTableMassActions({ selectionData, usersTableData }) {
  const { documentId, handleCloseModal, setModal } = useDocFlowContext()

  const { setPageLoader } = useDocFlowSendingContext()

  const { t } = useTranslation()

  const {
    filterValues,
    handleReload,
    items: tableItems,
    pagination,
  } = usersTableData

  const {
    excludeIds,
    handleCleanSelection,
    isAllSelected,
    selectAllPages,
    selectedIds,
  } = selectionData

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  function handleSuccessAction({ action, data, totalCount }) {
    handleCloseModal()
    handleCleanSelection()

    const successItemsCount = getMassActionsSuccessItemsLength(data)

    const notificationHandler =
      successItemsCount === 0
        ? handleAddErrorNotification
        : handleAddSuccessNotification

    handleReload().finally(() => {
      notificationHandler({
        title: getDocFlowSendingResultNotificationTitle({
          action,
          count: successItemsCount,
          t,
          totalCount,
        }),
      })

      setPageLoader(false)
    })
  }

  function handleErrorAction(
    title = 'Notification.DocumentStaplingSendingMassActionErrorTitleKey'
  ) {
    handleCloseModal()
    handleAddErrorNotification(title)
  }

  function massAction({ action, value = EMPTY_OBJECT }) {
    setPageLoader(true)

    const id = getMassActionIdParam({ selectAllPages, selectedIds })
    const excludeId = getMassActionExcludeIdParam({
      excludeIds,
      selectAllPages,
    })

    const params = {
      action,
      all: selectAllPages,
      filters: {
        ...filterValues,
        exclude_id: excludeId,
        id: getIdFilterParam({
          filterValues,
          ids: id,
          selectAllPages,
        }),
      },
      payload: value,
    }

    const countOfItems = selectAllPages ? pagination.count : id.length

    handleCloseModal()

    performDocFlowSendingMassAction(documentId, params)
      .then(({ data }) => {
        handleCleanSelection()

        handleSuccessAction({
          action,
          data,
          totalCount: countOfItems,
        })
      })
      .catch(() => {
        handleAddErrorNotification(
          'Notification.DocumentStaplingSendingMassActionErrorTitleKey'
        )

        setPageLoader(false)
      })
  }

  function getMassActionHandler({ action }) {
    return () => {
      massAction({ action })
    }
  }

  function handleConfirmMassAction({ action }) {
    setModal(
      <ConfirmationModal
        description={getSendingCommunicationTableMassConfirmationText(
          action,
          t
        )}
        onCancel={handleCloseModal}
        onSubmit={getMassActionHandler({ action })}
      />
    )
  }

  function getDeleteItemHandler({ action, item }) {
    return () => {
      setPageLoader(true)

      deleteSendingItem({ documentId, sendingId: item.id })
        .then(() => {
          handleSuccessAction({ action })
        })
        .catch(() => {
          handleErrorAction(
            t('Notification.DocumentStaplingSendingActionErrorKey')
          )
        })
        .finally(() => {
          setPageLoader(false)
        })
    }
  }

  function handleDelete({ action, item }) {
    if (isAllSelected || tableItems?.items?.length === 1) {
      setModal(
        <ConfirmationModal
          actionButtonsProps={{
            Cancel: null,
            submitText: t('Modal.CommonConfirmBtnKey'),
          }}
          description={t(
            getDocFlowSendingResultDeleteAttentionModalText(action)
          )}
          onCancel={handleCloseModal}
          onSubmit={handleCloseModal}
        />
      )
    } else {
      setModal(
        <ConfirmationModal
          actionButtonsProps={{
            submitText: t('Modal.DeleteBtnKey'),
          }}
          description={t('DocumentStapling.UploadingModalDeleteTextKey')}
          title={t('Modal.DeleteModalTitlekey')}
          onCancel={handleCloseModal}
          onSubmit={
            isDeleteMssAction(action)
              ? getMassActionHandler({ action })
              : getDeleteItemHandler({ action, item })
          }
        />
      )
    }
  }

  function setMassAction(action) {
    switch (action) {
      case DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_DELETE:
        handleDelete({ action })
        break

      case DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_ACTIVATE_SAVE_TO_PROFILE:
      case DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_DEACTIVATE_SAVE_TO_PROFILE:
      case DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_ACTIVATE_SEND_EMAIL:
      case DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_DEACTIVATE_SEND_EMAIL:
      case DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_ACTIVATE_SEND_ADDITIONAL_EMAIL:
      case DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_DEACTIVATE_SEND_ADDITIONAL_EMAIL:
      case DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_ACTIVATE_SEND_EPOST:
      case DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_DEACTIVATE_SEND_EPOST:
        handleConfirmMassAction({ action })
        break

      default:
        return null
    }
  }

  return {
    handleDelete,
    handleErrorAction,
    handleSuccessAction,
    setMassAction,
  }
}
